import React, { useState, useMemo, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useLang } from '../contexts/LanguageContext';
import { ui, serviceCategories, contact } from '../data/content';

const StarCanvas = () => {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let id;
    const resize = () => { canvas.width = canvas.offsetWidth; canvas.height = canvas.offsetHeight; };
    resize();
    const stars = Array.from({ length: 180 }, () => ({
      x: Math.random() * canvas.width, y: Math.random() * canvas.height,
      size: Math.random() * 2 + 0.2, phase: Math.random() * Math.PI * 2,
      speed: Math.random() * 0.02 + 0.008, color: Math.random() > 0.8 ? '#FF9933' : '#FFD700',
    }));
    let t = 0;
    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      t += 0.012;
      stars.forEach(s => {
        const op = 0.15 + 0.85 * Math.abs(Math.sin(t * s.speed + s.phase));
        ctx.globalAlpha = op; ctx.beginPath(); ctx.arc(s.x, s.y, s.size, 0, Math.PI * 2);
        ctx.fillStyle = s.color; ctx.fill();
        ctx.globalAlpha = 1;
      });
      id = requestAnimationFrame(draw);
    };
    draw();
    window.addEventListener('resize', resize);
    return () => { cancelAnimationFrame(id); window.removeEventListener('resize', resize); };
  }, []);
  return <canvas ref={canvasRef} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }} />;
};

const FloatingPetals = () => {
  const items = useMemo(() => Array.from({ length: 8 }, (_, i) => ({
    id: i, left: `${10 + i * 11}%`, delay: `${i * 0.9}s`, duration: `${8 + i % 3}s`,
    char: ['🌸', '🌺', '🌼', '🌹'][i % 4],
  })), []);
  return (
    <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'hidden', zIndex: 2 }}>
      {items.map(p => (
        <span key={p.id} className="petal" style={{ left: p.left, animationDelay: p.delay, animationDuration: p.duration }}>{p.char}</span>
      ))}
    </div>
  );
};

const categoryIcons = ['fa-landmark', 'fa-car', 'fa-briefcase', 'fa-shield-halved', 'fa-plane-departure', 'fa-palette', 'fa-clapperboard', 'fa-globe'];

export default function HeroSection() {
  const { lang } = useLang();
  const [searchQ, setSearchQ] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [listening, setListening] = useState(false);
  const recognitionRef = useRef(null);

  const allServices = useMemo(() => {
    const list = [];
    serviceCategories.forEach(cat => {
      cat.services.forEach(s => {
        list.push({ name: s[lang], nameOther: s[lang === 'hi' ? 'en' : 'hi'], category: cat[`title${lang === 'hi' ? 'Hi' : 'En'}`] });
      });
    });
    return list;
  }, [lang]);

  useEffect(() => {
    if (!searchQ.trim()) { setSuggestions([]); return; }
    const q = searchQ.toLowerCase().trim();
    const res = allServices.filter(s => {
      const nameHi = s[lang === 'hi' ? 'name' : 'nameOther'] || '';
      const nameEn = s[lang === 'hi' ? 'nameOther' : 'name'] || '';
      // Match Hindi directly (includes), and English case-insensitive
      return nameHi.includes(searchQ.trim()) || nameEn.toLowerCase().includes(q) ||
             s.name.includes(searchQ.trim()) || s.nameOther.toLowerCase().includes(q);
    }).slice(0, 6);
    setSuggestions(res);
  }, [searchQ, allServices, lang]);

  const startVoice = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;
    if (recognitionRef.current) { recognitionRef.current.abort(); }
    const rec = new SR();
    recognitionRef.current = rec;
    rec.lang = lang === 'hi' ? 'hi-IN' : 'en-IN';
    rec.interimResults = false;
    rec.onresult = (e) => { setSearchQ(e.results[0][0].transcript); setListening(false); };
    rec.onend = () => setListening(false);
    rec.onerror = () => setListening(false);
    rec.start();
    setListening(true);
  };

  const categories = serviceCategories.slice(0, 8);

  return (
    <section id="home" data-testid="hero-section"
      style={{
        position: 'relative', minHeight: '100vh', display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        background: 'radial-gradient(ellipse at 50% 20%, #1a2d5a 0%, #0A1128 55%, #050c1f 100%)',
        paddingTop: '120px', paddingBottom: '60px', overflow: 'hidden',
      }}>
      <StarCanvas />
      <FloatingPetals />

      {/* Temple light rays overlay */}
      <div className="temple-light" style={{ position: 'absolute', inset: 0, zIndex: 1 }} />

      {/* Diagonal golden accent lines */}
      <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', zIndex: 1, pointerEvents: 'none' }}>
        {[...Array(5)].map((_, i) => (
          <div key={i} style={{
            position: 'absolute', top: 0, left: `${10 + i * 20}%`,
            width: '1px', height: `${30 + i * 10}%`,
            background: `linear-gradient(to bottom, rgba(255,215,0,${0.08 + i * 0.02}), transparent)`,
            transform: `rotate(${-5 + i * 2}deg)`, transformOrigin: 'top center',
          }} />
        ))}
      </div>

      <div style={{ position: 'relative', zIndex: 5, textAlign: 'center', width: '100%', maxWidth: '1100px', padding: '0 20px' }}>
        {/* Main heading */}
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.2 }}>
          <p style={{ fontSize: 'clamp(11px,2vw,14px)', color: '#FF9933', letterSpacing: '4px', fontFamily: 'Rajdhani, sans-serif', marginBottom: '8px', textTransform: 'uppercase' }}>
            🚩 जय श्री राम 🚩
          </p>
          <h1 className="hindi shimmer-gold"
            style={{ fontSize: 'clamp(32px,7vw,72px)', fontWeight: 700, margin: '0 0 8px', lineHeight: 1.1, fontFamily: '"Yatra One", cursive' }}>
            श्री रणजीत सर्विसेस
          </h1>
          <p style={{ fontFamily: '"Cinzel Decorative", serif', fontSize: 'clamp(10px,2vw,16px)', color: '#FF9933', letterSpacing: '3px', marginBottom: '16px' }}>
            SHRI RANJEET SERVICES
          </p>
          <p className="hindi-body divine-glow-text"
            style={{ fontSize: 'clamp(14px,2.5vw,20px)', color: '#FFD700', letterSpacing: '1px', marginBottom: '28px' }}>
            {ui.hero.tagline[lang]}
          </p>
        </motion.div>

        {/* Service Category Badges */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 0.5 }}
          style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '10px', marginBottom: '28px' }}>
          {categories.map((cat, i) => (
            <motion.button key={cat.id}
              initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.5 + i * 0.07 }}
              onClick={() => document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' })}
              data-testid={`hero-badge-${cat.id}`}
              style={{
                background: `rgba(${cat.id === 'travel' ? '255,153,0' : cat.id === 'business' ? '80,200,120' : cat.id === 'insurance' ? '210,4,45' : '255,215,0'},0.1)`,
                border: `1px solid ${cat.color}40`, color: cat.color,
                padding: '7px 16px', borderRadius: '25px', cursor: 'pointer',
                fontFamily: 'Khand, sans-serif', fontSize: '13px', letterSpacing: '0.5px',
                backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', gap: '6px',
              }}
              whileHover={{ scale: 1.05, boxShadow: `0 0 15px ${cat.color}40` }}>
              <i className={`fa-solid ${cat.iconClass}`} style={{ fontSize: '12px' }} />
              {lang === 'hi' ? cat.titleHi : cat.titleEn}
            </motion.button>
          ))}
        </motion.div>

        {/* Tour package highlight */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.8 }}>
          <div className="glass-card antique-corner"
            style={{ maxWidth: '680px', margin: '0 auto 28px', padding: '14px 24px', borderRadius: '12px', textAlign: 'center' }}>
            <i className="fa-solid fa-earth-asia" style={{ color: '#FF9933', marginRight: '8px' }} />
            <span className="hindi-body" style={{ fontSize: 'clamp(12px,1.8vw,15px)', color: '#FFF3B0' }}>
              {ui.hero.tourLine[lang]}
            </span>
          </div>
        </motion.div>

        {/* CTA Buttons */}
        <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.0 }}
          style={{ display: 'flex', gap: '14px', justifyContent: 'center', flexWrap: 'wrap', marginBottom: '36px' }}>
          <a href={`https://wa.me/${contact.whatsapp}`} target="_blank" rel="noopener noreferrer"
            data-testid="hero-whatsapp-btn"
            className="btn-whatsapp"
            style={{ padding: '13px 28px', borderRadius: '50px', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '15px', fontFamily: 'Rajdhani, sans-serif' }}>
            <i className="fa-brands fa-whatsapp" style={{ fontSize: '18px' }} />
            {ui.hero.whatsapp[lang]}
          </a>
          <a href={`tel:${contact.phone}`} data-testid="hero-call-btn"
            className="btn-contact"
            style={{ padding: '13px 28px', borderRadius: '50px', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '15px', fontFamily: 'Rajdhani, sans-serif' }}>
            <i className="fa-solid fa-phone" style={{ fontSize: '16px' }} />
            {ui.hero.callNow[lang]}
          </a>
          <button onClick={() => document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' })}
            data-testid="hero-services-btn"
            style={{
              padding: '13px 28px', borderRadius: '50px', fontSize: '15px',
              background: 'transparent', border: '1px solid rgba(255,215,0,0.4)',
              color: '#FFD700', cursor: 'pointer', fontFamily: 'Rajdhani, sans-serif',
              display: 'flex', alignItems: 'center', gap: '8px',
            }}>
            <i className="fa-solid fa-grid-2" style={{ fontSize: '14px' }} />
            {ui.hero.allServices[lang]}
          </button>
        </motion.div>

        {/* AI Search Bar */}
        <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.2 }}
          style={{ maxWidth: '600px', margin: '0 auto', position: 'relative' }}>
          <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
            <i className="fa-solid fa-magnifying-glass" style={{ position: 'absolute', left: '18px', color: 'rgba(255,215,0,0.5)', fontSize: '16px', zIndex: 1 }} />
            <input
              data-testid="search-input"
              type="text"
              value={searchQ}
              onChange={e => setSearchQ(e.target.value)}
              placeholder={ui.hero.searchPlaceholder[lang]}
              className="neon-input"
              style={{ width: '100%', padding: '14px 55px 14px 48px', borderRadius: '50px', fontSize: '15px', fontFamily: 'Khand, sans-serif', boxSizing: 'border-box' }}
            />
            <button onClick={startVoice} data-testid="voice-search-btn"
              style={{
                position: 'absolute', right: '12px', background: listening ? 'rgba(80,200,120,0.2)' : 'rgba(255,215,0,0.1)',
                border: `1px solid ${listening ? '#50C878' : 'rgba(255,215,0,0.3)'}`,
                borderRadius: '50%', width: '34px', height: '34px', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: listening ? '#50C878' : '#FFD700',
              }}>
              <i className={`fa-solid fa-${listening ? 'circle-dot' : 'microphone'}`} style={{ fontSize: '14px' }} />
            </button>
          </div>

          {/* Suggestions dropdown */}
          {suggestions.length > 0 && (
            <div style={{
              position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50,
              background: 'rgba(13,22,50,0.98)', border: '1px solid rgba(255,215,0,0.3)',
              borderRadius: '12px', overflow: 'hidden', marginTop: '6px',
              boxShadow: '0 10px 40px rgba(0,0,0,0.5)',
            }}>
              {suggestions.map((s, i) => (
                <button key={i} onClick={() => { setSearchQ(s.name); setSuggestions([]); document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' }); }}
                  style={{
                    display: 'block', width: '100%', textAlign: 'left', padding: '10px 18px',
                    background: 'none', border: 'none', cursor: 'pointer',
                    color: '#FFF3B0', fontFamily: 'Khand, sans-serif', fontSize: '14px',
                    borderBottom: '1px solid rgba(255,215,0,0.08)',
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,215,0,0.08)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                  <i className="fa-solid fa-arrow-right" style={{ color: '#FF9933', marginRight: '8px', fontSize: '11px' }} />
                  {s.name} <span style={{ color: 'rgba(255,153,0,0.6)', fontSize: '11px', marginLeft: '6px' }}>— {s.category}</span>
                </button>
              ))}
            </div>
          )}
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}
        style={{ position: 'absolute', bottom: '24px', left: '50%', transform: 'translateX(-50%)', zIndex: 5, color: 'rgba(255,215,0,0.5)', fontSize: '20px' }}>
        <i className="fa-solid fa-chevron-down" />
      </motion.div>
    </section>
  );
}
